// ─────────────────────────────────────────────────────────────────────
// SERIESPILL RESULT HELPERS
//
// Pure result/test-setup helpers for the Seriespill (league play) format,
// lifted verbatim out of legacy/App.jsx (PR-AT). applySerieResult runs the
// shared chronological KM ELO recalc (recalcKMElo) so the view and the
// banepad use identical logic; buildSeriespillTestClasses builds a quick,
// valid admin test setup (PRNG shuffle, no engine call).
// ─────────────────────────────────────────────────────────────────────
import { recalcKMElo } from './kmResults.js';
import { resolveEffects, type EngineEffects } from './effects.js';

// DELIBERATELY RANDOM (#203 leaves this alone): this is the admin "generate a
// test setup" button. Shuffling IS its purpose, and it never runs on the #183
// server path — it builds draft configuration for a human to look at, not a
// result anyone replays. Making it injectable would add a parameter nobody has
// a reason to pass.
export function buildSeriespillTestClasses(
  allPlayers: any,
  { perDiv = 8, genders = ['M'] }: any = {},
) {
  // Build a valid test setup that hits the guideline of 4-10 pairs per division.
  // perDiv 8 is the default = middle of the sweet spot. Shuffles players, distributes them into classes
  // (each class gets its own portion), then pairs, then divisions of perDiv each.
  const GENDER_LABEL = { M: 'Herrer', F: 'Damer', X: 'Mix' };
  const shuffled = [...(allPlayers || [])];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  const classes = [];
  // Split the players almost evenly between the selected classes
  const perClass = Math.floor(shuffled.length / Math.max(1, genders.length));
  genders.forEach((g, ci) => {
    const slice = shuffled.slice(ci * perClass, (ci + 1) * perClass);
    const pairs = [];
    for (let i = 0; i + 1 < slice.length; i += 2) {
      pairs.push({ p1Id: slice[i].id, p2Id: slice[i + 1].id });
    }
    const divisions = [];
    let level = 1;
    for (let i = 0; i < pairs.length; i += perDiv) {
      const chunk = pairs.slice(i, i + perDiv);
      // If this chunk is small (< 4 pairs) and there is an earlier division
      // that can take more without exceeding the upper limit — merge instead of
      // creating a new small division below the sweet spot.
      if (chunk.length < 4 && divisions.length > 0) {
        divisions[divisions.length - 1].pairs.push(...chunk);
        break;
      }
      if (chunk.length < 2) {
        if (divisions.length) divisions[divisions.length - 1].pairs.push(...chunk);
        break;
      }
      divisions.push({
        name: `${level}. divisjon`,
        level,
        promote: 2,
        relegate: 2,
        starpoint: level === 1,
        pairs: chunk,
      });
      level++;
    }
    if (divisions.length > 0) {
      classes.push({
        id: `cls_${g.toLowerCase()}_${Date.now()}_${ci}`,
        name: GENDER_LABEL[g] || g,
        gender: g,
        divisions,
      });
    }
  });
  return classes;
}

export function applySerieResult(
  series: any,
  updatedMatch: any,
  allPlayers: any = [],
  effects?: EngineEffects,
) {
  // One clock read for the whole application (#203). Note that the ELO here is
  // ALREADY clock-independent: recalcKMElo replays the division chronologically
  // through calcEloChange, which takes no `now` and applies no newbie boost. So
  // the only impurity in this path was the completedAt stamp below.
  const { now, settings } = resolveEffects(effects);
  const pairOf = (pairId, divId) =>
    (series.pairs || []).find((p) => p.id === pairId && p.divisionId === divId) ||
    (series.pairs || []).find((p) => p.id === pairId);
  const nextMatches = (series.matches || []).map((m) => {
    if (m.id !== updatedMatch.id) return m;
    // Precedence: the STORED stamp, then one the caller supplied, then the
    // instant. The middle term is the #203 fix. Previously the explicit key
    // beat the `...updatedMatch` spread unconditionally, so a completedAt on
    // the incoming match was DISCARDED and a retry restamped it — and because
    // recalcKMElo orders played matches by completedAt, a restamp moves the
    // match to the end of the division's chronology and changes every rating
    // computed after it. Keeping the stored stamp first preserves the property
    // that correcting an already-played match is stable.
    const merged = {
      ...m,
      ...updatedMatch,
      completedAt: m.completedAt || updatedMatch.completedAt || now,
    };
    if (!merged.roster) {
      const t1 = pairOf(merged.team1?.pairId, merged.divisionId);
      const t2 = pairOf(merged.team2?.pairId, merged.divisionId);
      if (t1 && t2) merged.roster = { t1: [t1.p1Id, t1.p2Id], t2: [t2.p1Id, t2.p2Id] };
    }
    return merged;
  });
  let next = { ...series, matches: nextMatches };
  if (typeof recalcKMElo === 'function') {
    const recalced = recalcKMElo(next, allPlayers, settings);
    if (recalced?.matches) next = { ...next, matches: recalced.matches };
  }
  return next;
}
